import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { messagingApi, type MailboxBoxes } from '../api/endpoints';
import { messageFor } from '../api/messages';
import { AskTeamForm } from '../pages/inbox';
import { TeamComposeForm, TeamComposePage } from '../pages/teamInbox';
import { Alert, Button, PageHeader } from './ui';

interface Option {
  eventId: string;
  title: string;
  organizationName: string;
  /** `team`: Person, Gruppe oder Organisation. `mine`: nur der Veranstalter des Basars. */
  kind: 'team' | 'mine';
  canBroadcast: boolean;
  organizationId: string | null;
}

function optionsOf(boxes: MailboxBoxes): Option[] {
  const team: Option[] = boxes.team.map((box) => ({
    eventId: box.eventId,
    title: box.eventTitle,
    organizationName: box.organizationName,
    kind: 'team',
    canBroadcast: box.canBroadcast,
    organizationId: box.organizationId,
  }));
  const own = new Set(team.map((option) => option.eventId));
  const mine: Option[] = boxes.mine
    .filter((contact) => !own.has(contact.eventId))
    .map((contact) => ({
      eventId: contact.eventId,
      title: contact.eventTitle,
      organizationName: contact.organizationName,
      kind: 'mine',
      canBroadcast: false,
      organizationId: null,
    }));
  return [...team, ...mine];
}

/**
 * „Neue Nachricht“ in drei Schritten (Plan 16, 4.13): 1. Basar wählen, 2. Empfänger wählen (Person oder Gruppe),
 * 3. Betreff und Text. Gibt es nur einen Basar, entfällt Schritt 1. Die Wahl folgt den Postfächern, die der Server
 * meldet (`/me/messages/mailbox/boxes`); geprüft wird beim Senden weiter dort. `?basar=` und `?betreff=` belegen vor.
 * Organisations-Rundschreiben haben eine eigene Seite und sind immer als Link erreichbar.
 * `fixed`: unter `/basare/:eventId/nachrichten/neu`, der Basar steht fest (Schritt 1 entfällt). Ohne Postfach-Recht
 * (z. B. Support-Zugriff) bleibt dort die bisherige Seite.
 */
export function ComposeSheet({ fixed = false }: { fixed?: boolean }) {
  const routeEventId = useParams()['eventId'];
  const fixedId = fixed ? (routeEventId ?? '') : null;
  const [params] = useSearchParams();
  const [boxes, setBoxes] = useState<MailboxBoxes | null>(null);
  const [error, setError] = useState<string | null>(null);
  // `undefined`: aus der Adresse bzw. dem einzigen Basar ableiten, `null`: Schritt 1 wurde wieder geöffnet
  const [picked, setPicked] = useState<string | null | undefined>(undefined);
  const [group, setGroup] = useState(false);
  const stepHeading = useRef<HTMLHeadingElement>(null);
  const mounted = useRef(false);

  useEffect(() => {
    const guard = { cancelled: false };
    messagingApi.mailboxBoxes().then(
      (loaded) => {
        if (!guard.cancelled) setBoxes(loaded);
      },
      (caught: unknown) => {
        if (!guard.cancelled) setError(messageFor(caught));
      },
    );
    return () => {
      guard.cancelled = true;
    };
  }, []);

  const options = boxes === null ? [] : optionsOf(boxes);
  const fromAddress = fixedId ?? params.get('basar');
  const derived =
    options.find((option) => option.eventId === fromAddress)?.eventId ??
    (options.length === 1 ? (options[0]?.eventId ?? null) : null);
  const selectedId = picked === undefined || fixedId !== null ? derived : picked;
  const selected = options.find((option) => option.eventId === selectedId) ?? null;
  const orgs = boxes?.orgs ?? [];

  // Nach einem Schrittwechsel steht der Fokus auf der neuen Überschrift (nicht auf der verschwundenen Auswahl)
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    stepHeading.current?.focus();
  }, [selected?.eventId]);

  const pick = (eventId: string | null): void => {
    setPicked(eventId);
    setGroup(false);
  };

  // Mit festem Basar ohne Team-Postfach (Support-Zugriff, Fehler): die bisherige Seite
  if (fixedId !== null && (error !== null || (boxes !== null && selected?.kind !== 'team'))) {
    return <TeamComposePage />;
  }

  const back =
    fixedId === null
      ? { to: '/nachrichten', label: 'Nachrichten' }
      : { to: `/basare/${fixedId}/nachrichten`, label: 'Nachrichten' };
  const orgLinks = orgs.length > 0 && (
    <section className="stack" aria-label="Rundschreiben an eine Organisation">
      <h2>Rundschreiben an eine Organisation</h2>
      <ul className="list">
        {orgs.map((org) => (
          <li key={org.organizationId}>
            <Link to={`/organisationen/${org.organizationId}/nachrichten/neu`}>
              Rundschreiben der Organisation {org.organizationName}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );

  return (
    <div className="stack stack--lg">
      <PageHeader back={back} title="Neue Nachricht" />
      {error !== null && <Alert kind="error">{error}</Alert>}
      {boxes === null ? (
        error === null && <p role="status">Einen Moment bitte …</p>
      ) : options.length === 0 ? (
        <>
          <Alert kind="info">
            Sie können dem Veranstalter schreiben, sobald Sie sich zu einem Basar angemeldet haben
            oder als Helfer eingetragen sind.
          </Alert>
          {orgLinks}
        </>
      ) : selected === null ? (
        <>
          <section className="stack" aria-labelledby="schritt-basar">
            <h2 id="schritt-basar" tabIndex={-1} ref={stepHeading}>
              1. Für welchen Basar?
            </h2>
            <ul className="stack mailbox__choices">
              {options.map((option) => (
                <li key={option.eventId}>
                  <button
                    type="button"
                    className="btn btn--secondary mailbox__choice"
                    onClick={() => {
                      pick(option.eventId);
                    }}
                  >
                    <span>
                      {option.title} <span className="muted">· {option.organizationName}</span>
                      {option.kind === 'mine' && (
                        <span className="muted"> · an den Veranstalter</span>
                      )}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
          {orgLinks}
        </>
      ) : (
        <>
          <p className="row">
            <span>
              <strong>{selected.title}</strong>{' '}
              <span className="muted">· {selected.organizationName}</span>
            </span>
            {options.length > 1 && fixedId === null && (
              <Button
                variant="secondary"
                small
                onClick={() => {
                  pick(null);
                }}
              >
                Basar ändern
              </Button>
            )}
          </p>
          {selected.kind === 'mine' ? (
            <>
              <h2 tabIndex={-1} ref={stepHeading}>
                2. Betreff und Text
              </h2>
              <AskTeamForm
                key={selected.eventId}
                eventId={selected.eventId}
                initialSubject={params.get('betreff') ?? ''}
              />
            </>
          ) : (
            <>
              <h2 tabIndex={-1} ref={stepHeading}>
                2. An wen?
              </h2>
              <div className="chips" role="group" aria-label="Art des Empfängers">
                <button
                  type="button"
                  className="chip"
                  aria-pressed={!group || !selected.canBroadcast}
                  onClick={() => {
                    setGroup(false);
                  }}
                >
                  Eine Person
                </button>
                {selected.canBroadcast && (
                  <button
                    type="button"
                    className="chip"
                    aria-pressed={group}
                    onClick={() => {
                      setGroup(true);
                    }}
                  >
                    Eine Gruppe
                  </button>
                )}
              </div>
              <TeamComposeForm
                key={`${selected.eventId}:${String(group && selected.canBroadcast)}`}
                eventId={selected.eventId}
                group={group && selected.canBroadcast}
                threadBase={
                  fixedId === null
                    ? `/nachrichten/basar/${selected.eventId}`
                    : `/basare/${fixedId}/nachrichten`
                }
                initialSubject={params.get('betreff') ?? ''}
                textHeading="3. Betreff und Text"
              />
            </>
          )}
          {orgLinks}
        </>
      )}
    </div>
  );
}
